/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import {
  TemplateCatalogItem,
  TemplateCatalogDetail,
  UpdateTemplateMetadataRequest,
} from '@/types/superadmin.types';

const BASE_URL = '/admin/templates';

// ── List / Get ──────────────────────────────────────────────────

export const getTemplateCatalog = () =>
  get<ApiResponse<TemplateCatalogItem[]>>(BASE_URL);

export const getTemplateDetail = (id: string) =>
  get<ApiResponse<TemplateCatalogDetail>>(`${BASE_URL}/${id}`);

// ── Upload ──────────────────────────────────────────────────────

export const uploadTemplate = (data: FormData) =>
  post<ApiResponse<TemplateCatalogItem>>(BASE_URL, data);

// ── Update Metadata ─────────────────────────────────────────────

export const updateTemplateMetadata = (id: string, data: UpdateTemplateMetadataRequest) =>
  put<ApiResponse<TemplateCatalogItem>>(`${BASE_URL}/${id}`, data);

// ── Re-upload JRXML ─────────────────────────────────────────────

export const updateJrxml = (id: string, data: FormData) =>
  put<ApiResponse<TemplateCatalogItem>>(`${BASE_URL}/${id}/jrxml`, data);

// ── Letterhead ──────────────────────────────────────────────────

export const updateLetterhead = (id: string, data: FormData) =>
  put<ApiResponse<TemplateCatalogItem>>(`${BASE_URL}/${id}/letterhead`, data);

export const removeLetterhead = (id: string) =>
  del<ApiResponse<TemplateCatalogItem>>(`${BASE_URL}/${id}/letterhead`);

// ── Delete ──────────────────────────────────────────────────────

export const deleteTemplate = (id: string) =>
  del<ApiResponse<void>>(`${BASE_URL}/${id}`);

// ── Preview ─────────────────────────────────────────────────────

export const getPreviewUrl = (id: string) =>
  `${BASE_URL}/${id}/preview`;

export const getPreviewUrlByCode = (code: string) =>
  `${BASE_URL}/code/${code}/preview`;

// ── Regenerate Preview ──────────────────────────────────────────

export const regeneratePreview = (id: string) =>
  post<ApiResponse<TemplateCatalogItem>>(`${BASE_URL}/${id}/regenerate-preview`);

// ── Download JRXML ──────────────────────────────────────────────

export const getDownloadJrxmlUrl = (id: string) =>
  `${BASE_URL}/${id}/download-jrxml`;
